import type { ComponentPropsWithoutRef } from "react";
import { forwardRef, type ForwardRefRenderFunction } from "react";
import { type Account } from "../../../../portals/messagemedia/hooks";
import { useSidebarState } from "../../../NavigationBar/state";
import { randInRange } from "../../../NavigationBar/utils/helpers";
import { useAccountSwitcherContext } from "../../AccountSwitcherContext";
import { StyledTrigger } from "./styled";
import "@nectary/components/tooltip";

/** Props for {@link AccountSwitcherLogo} */
export type AccountSwitcherLogo = ComponentPropsWithoutRef<"div"> & {
  size?: "sm" | "lg";
  accountName?: Account["accountName"];
  accountId?: Account["accountId"];
  showTooltip?: boolean;
  clickable?: boolean;
  type?: 1 | 2 | 3 | 4 | 5;
};

const AccountSwitcherLogoNoRef: ForwardRefRenderFunction<
  HTMLDivElement,
  AccountSwitcherLogo
> = (
  {
    size = "lg",
    accountName,
    accountId,
    className,
    showTooltip,
    clickable = false,
    type,
    ...props
  },
  ref
) => {
  const { currentAccount, hasOnlyOneAccount } = useAccountSwitcherContext();
  const { isCollapsed } = useSidebarState();
  const renderName = (name: string) => {
    const names = name
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .split(" ")
      .slice(0, 2);
    return names
      .map((n) => n.charAt(0))
      .join("")
      .toLocaleUpperCase();
  };

  const name =
    accountName ||
    currentAccount?.accountName ||
    currentAccount?.accountId ||
    accountId ||
    "";
  const colorType = hasOnlyOneAccount ? 1 : type || randInRange(name, 0, 4);

  if (!showTooltip) {
    return (
      <StyledTrigger
        ref={ref}
        className={className}
        {...props}
        $size={size}
        $clickable={clickable}
        isCollapsed={isCollapsed}
        $type={colorType}
      >
        {renderName(name)}
      </StyledTrigger>
    );
  }

  return (
    <sinch-tooltip type="fast" text={name} orientation={"right"}>
      <StyledTrigger
        ref={ref}
        className={className}
        {...props}
        $size={size}
        isCollapsed={isCollapsed}
        $clickable={clickable}
        $type={colorType}
      >
        {renderName(name)}
      </StyledTrigger>
    </sinch-tooltip>
  );
};

export const AccountSwitcherLogo = forwardRef(AccountSwitcherLogoNoRef);
